import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense } from "react";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import DashboardLayout from "./components/DashboardLayout";
import { RequireAdmin } from "./components/RequireAdmin";

const Home = lazy(() => import("./pages/Home"));
const PersonalDashboard = lazy(() => import("./pages/PersonalDashboard"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const InboxPage = lazy(() => import("./pages/InboxPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPage = lazy(() => import("./pages/LegalPage").then((module) => ({ default: module.PrivacyPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const TermsPage = lazy(() => import("./pages/LegalPage").then((module) => ({ default: module.TermsPage })));
const SupportPage = lazy(() => import("./pages/LegalPage").then((module) => ({ default: module.SupportPage })));
const SetupNamePage = lazy(() => import("./pages/SetupNamePage"));

function PageFallback() {
  return (
    <div
      className="mx-auto mt-10 h-1 w-24 animate-pulse rounded bg-primary/30"
      role="status"
      aria-label="Loading page"
    />
  );
}

function Router() {
  return (
    <Switch>
      {/* Public pages — no auth gate (required for App Store review links). */}
      <Route path={"/privacy"} component={PrivacyPage} />
      <Route path={"/terms"} component={TermsPage} />
      <Route path={"/support"} component={SupportPage} />
      <Route path={"/setup-name"} component={SetupNamePage} />
      <Route>
        <DashboardLayout>
          <Switch>
            <Route path={"/"} component={Home} />
            <Route path={"/dashboard"} component={PersonalDashboard} />
            <Route path={"/projects"} component={ProjectsPage} />
            <Route path={"/inbox"} component={InboxPage} />
            <Route path={"/projects/:id/tasks/:taskId"} component={ProjectDetail} />
            <Route path={"/projects/:id"} component={ProjectDetail} />
            <Route path={"/users"}>
              <RequireAdmin>
                <UsersPage />
              </RequireAdmin>
            </Route>
            <Route path={"/reports"}>
              <RequireAdmin>
                <ReportsPage />
              </RequireAdmin>
            </Route>
            <Route path={"/404"} component={NotFound} />
            <Route component={NotFound} />
          </Switch>
        </DashboardLayout>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster richColors position="top-center" />
          <Suspense fallback={<PageFallback />}>
            <Router />
          </Suspense>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
